"use client";

import { Autocomplete as AutocompletePrimitive } from "@base-ui/react/autocomplete";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Icon } from "./icon";
import { Input, type InputProps } from "./input";
import { ScrollArea } from "./scroll-area";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./tooltip";
import type { Omittable } from "../../lib/props";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";

/**
 * The autocomplete root. On a coarse pointer the popup opens MODAL unless
 * the caller decides otherwise — the same rule as `Popover`: Base UI mounts
 * a transparent backdrop under the popup, cut out around the input group,
 * so the tap that closes the list is spent there and never lands on what
 * sat beneath the finger, while the field itself stays tappable. An inline
 * list (`Command`) has no positioner, so nothing changes there. A mouse
 * keeps the non-modal default.
 */
function AutocompleteRoot({
  modal,
  ...props
}: AutocompletePrimitive.Root.Props<unknown>): React.ReactElement {
  const touch = useIsTouchDevice();
  return <AutocompletePrimitive.Root modal={modal ?? touch} {...props} />;
}

// Cast back to the primitive's overloads so grouped and flat item typings
// survive for every caller.
export const Autocomplete = AutocompleteRoot as typeof AutocompletePrimitive.Root;

export function AutocompleteInput({
  className,
  showTrigger = false,
  showClear = false,
  startAddon,
  startAction,
  endAddon,
  size,
  variant,
  backgroundColor,
  borderColor,
  triggerProps,
  clearProps,
  clearTooltip,
  ...props
}: Omit<AutocompletePrimitive.Input.Props, "size"> & Omittable<{
  showTrigger?: boolean;
  showClear?: boolean;
  startAddon?: React.ReactNode;
  /** An interactive control at the input's start cap — a Button on the icon
   *  rung one step under the field's (`sm`→`icon-xs`, `default`→`icon-sm`,
   *  `lg`→`icon`, `xl`→`icon-lg`, `2xl`→`icon-xl`), so it rests inside the
   *  field with the clear's margin on every side. Unlike `startAddon` it is
   *  reachable: it keeps its pointer events and its accessible name, and the
   *  typed text starts past it. The two slots are exclusive; the action
   *  wins. */
  startAction?: React.ReactNode;
  /** Decorative adornment at the input's end cap (e.g. a search glyph).
   *  It yields the slot whenever the trigger or the clear affordance is
   *  showing, so `showClear` swaps it for the × while a value is present. */
  endAddon?: React.ReactNode;
  size?: "sm" | "default" | "lg" | "xl" | "2xl" | number;
  variant?: InputProps["variant"];
  backgroundColor?: InputProps["backgroundColor"];
  borderColor?: InputProps["borderColor"];
  ref?: React.Ref<HTMLInputElement>;
  triggerProps?: AutocompletePrimitive.Trigger.Props;
  clearProps?: AutocompletePrimitive.Clear.Props;
  /** Tooltip content for the clear affordance (requires `showClear`).
   *  Omitted = no tooltip, so consumers without a TooltipProvider are
   *  unaffected. */
  clearTooltip?: React.ReactNode;
}>): React.ReactElement {
  const sizeValue = (size ?? "default") as
    | "sm"
    | "default"
    | "lg"
    | "xl"
    | "2xl"
    | number;
  const roomy = sizeValue === "xl" || sizeValue === "2xl";
  // Anchors the clear tooltip to the BUTTON: the tooltip trigger is a
  // boxless span (see below), whose zero-rect would position the popup at
  // the viewport origin.
  const clearRef = React.useRef<HTMLButtonElement | null>(null);
  // The action's inset is the margin its rung leaves inside the field —
  // the roomy rungs are 4px taller under `sm`, so their inset steps with
  // the breakpoint. The text inset clears the action plus a 6–8px gap.
  const startActionInset =
    sizeValue === "2xl"
      ? "start-1.5 sm:start-1"
      : sizeValue === "xl"
        ? "start-1 sm:start-0.5"
        : "start-0.5";
  const startActionTextInset =
    sizeValue === "2xl"
      ? "*:data-[slot=autocomplete-input]:ps-13"
      : sizeValue === "xl"
        ? "*:data-[slot=autocomplete-input]:ps-11"
        : sizeValue === "lg"
          ? "*:data-[slot=autocomplete-input]:ps-10"
          : sizeValue === "sm"
            ? "*:data-[slot=autocomplete-input]:ps-8"
            : "*:data-[slot=autocomplete-input]:ps-9";
  const leadingAddon = startAction ? undefined : startAddon;

  return (
    <AutocompletePrimitive.InputGroup
      className="relative not-has-[>*.w-full]:w-fit w-full text-foreground has-disabled:opacity-64"
      data-slot="autocomplete-input-group"
    >
      {leadingAddon && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 start-px z-10 flex items-center ps-[calc(--spacing(3)-1px)] opacity-80 has-[+[data-size=sm]]:ps-[calc(--spacing(2.5)-1px)] [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:-mx-0.5"
          data-slot="autocomplete-start-addon"
        >
          {leadingAddon}
        </div>
      )}
      {startAction && (
        <div
          className={cn(
            "absolute inset-y-0 z-10 flex items-center",
            startActionInset,
          )}
          data-slot="autocomplete-start-action"
        >
          {startAction}
        </div>
      )}
      <AutocompletePrimitive.Input
        className={cn(
          startAction && startActionTextInset,
          leadingAddon &&
            "data-[size=sm]:*:data-[slot=autocomplete-input]:ps-[calc(--spacing(7.5)-1px)] *:data-[slot=autocomplete-input]:ps-[calc(--spacing(8.5)-1px)] sm:data-[size=sm]:*:data-[slot=autocomplete-input]:ps-[calc(--spacing(7)-1px)] sm:*:data-[slot=autocomplete-input]:ps-[calc(--spacing(8)-1px)]",
          // An end addon reserves the mirror of the start addon's clearance,
          // unconditionally: the clear that replaces it fits the same slot.
          endAddon
            ? "data-[size=sm]:*:data-[slot=autocomplete-input]:pe-[calc(--spacing(7.5)-1px)] *:data-[slot=autocomplete-input]:pe-[calc(--spacing(8.5)-1px)] sm:data-[size=sm]:*:data-[slot=autocomplete-input]:pe-[calc(--spacing(7)-1px)] sm:*:data-[slot=autocomplete-input]:pe-[calc(--spacing(8)-1px)]"
            : sizeValue === "sm"
              ? "has-[+[data-slot=autocomplete-trigger],+[data-slot=autocomplete-clear]]:*:data-[slot=autocomplete-input]:pe-6.5"
              : roomy
                ? "has-[+[data-slot=autocomplete-trigger],+[data-slot=autocomplete-clear]]:*:data-[slot=autocomplete-input]:pe-10"
                : "has-[+[data-slot=autocomplete-trigger],+[data-slot=autocomplete-clear]]:*:data-[slot=autocomplete-input]:pe-7",
          className,
        )}
        data-slot="autocomplete-input"
        render={
          <Input
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            nativeInput
            size={sizeValue}
            variant={variant}
          />
        }
        {...props}
      />
      {endAddon && (
        <div
          aria-hidden="true"
          // `:has(~ …)` reads the later siblings. The clear is gated on its
          // `data-visible` state, not its presence: Base UI keeps the button
          // mounted through its exit, and the tooltip wrapper span (which
          // repeats the clear's data-slot) is always mounted — the second
          // selector reaches the visible button through that wrapper.
          className={cn(
            "pointer-events-none absolute inset-y-0 end-px z-10 flex items-center opacity-80 has-[~[data-slot=autocomplete-clear][data-visible]]:hidden has-[~[data-slot=autocomplete-clear]_[data-visible]]:hidden has-[~[data-slot=autocomplete-trigger]]:hidden [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:-mx-0.5",
            sizeValue === "sm"
              ? "pe-[calc(--spacing(2.5)-1px)]"
              : "pe-[calc(--spacing(3)-1px)]",
          )}
          data-slot="autocomplete-end-addon"
        >
          {endAddon}
        </div>
      )}
      {showTrigger && (
        <AutocompleteTrigger
          // The chevron is the only content, so the button has no name of its
          // own. `triggerProps` spreads after and overrides it.
          aria-label="Show suggestions"
          className={cn(
            "absolute top-1/2 inline-flex size-8 shrink-0 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-transparent opacity-80 outline-none transition-colors pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:-translate-x-1/2 pointer-coarse:after:-translate-y-1/2 pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 hover:opacity-100 has-[+[data-slot=autocomplete-clear]]:hidden sm:size-7 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
            sizeValue === "sm" ? "end-0" : roomy ? "end-2" : "end-0.5",
          )}
          {...triggerProps}
        >
          <AutocompletePrimitive.Icon data-slot="autocomplete-icon">
            <Icon name="chevrons-up-down" />
          </AutocompletePrimitive.Icon>
        </AutocompleteTrigger>
      )}
      {showClear &&
        (() => {
          // clearProps.className MERGES onto the base classes (a consumer
          // nudging the offset must not have to restate the whole recipe).
          const { className: clearClassName, ...restClearProps } =
            clearProps ?? {};
          const clear = (
            <AutocompleteClear
              className={cn(
                "absolute top-1/2 -translate-y-1/2 has-[+[data-slot=autocomplete-clear]]:hidden",
                // The end inset matches the 20px circle's vertical margin in
                // each field height, so the circle clears the corner radius
                // instead of colliding with the border and focus ring.
                sizeValue === "sm" ? "end-1" : roomy ? "end-2" : "end-1.5",
                clearClassName,
              )}
              {...restClearProps}
              ref={clearRef}
            />
          );
          if (!clearTooltip) return clear;
          return (
            <Tooltip>
              {/* The trigger wraps the clear instead of rendering AS it —
                  composing Trigger into the Clear part drops the trigger's
                  ref/listeners and the tooltip never opens. The span is
                  boxless (`contents`) so it repeats the clear's data-slot for
                  the input's `+[data-slot=autocomplete-clear]` sibling rules —
                  and, having no rect, the popup must anchor to the button
                  itself or it renders at the viewport origin. */}
              <TooltipTrigger
                data-slot="autocomplete-clear"
                render={<span className="contents" />}
              >
                {clear}
              </TooltipTrigger>
              <TooltipPopup anchor={clearRef}>{clearTooltip}</TooltipPopup>
            </Tooltip>
          );
        })()}
    </AutocompletePrimitive.InputGroup>
  );
}

export function AutocompletePopup({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  alignOffset,
  align = "start",
  anchor,
  portalProps,
  ...props
}: AutocompletePrimitive.Popup.Props & Omittable<{
  align?: AutocompletePrimitive.Positioner.Props["align"];
  sideOffset?: AutocompletePrimitive.Positioner.Props["sideOffset"];
  alignOffset?: AutocompletePrimitive.Positioner.Props["alignOffset"];
  side?: AutocompletePrimitive.Positioner.Props["side"];
  anchor?: AutocompletePrimitive.Positioner.Props["anchor"];
  portalProps?: AutocompletePrimitive.Portal.Props;
}>): React.ReactElement {
  return (
    <AutocompletePrimitive.Portal {...portalProps}>
      <AutocompletePrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="z-50 select-none"
        data-slot="autocomplete-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <span
          className={cn(
            "relative flex max-h-full min-w-(--anchor-width) max-w-(--available-width) origin-(--transform-origin) rounded-lg border bg-popover not-dark:bg-clip-padding shadow-popover transition-[scale,opacity] before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
            className,
          )}
          // Portalled, so it inherits no surface scope — names its own, and
          // descendant `border-border` draws the popover-tuned hairline.
          data-on="popover"
        >
          <AutocompletePrimitive.Popup
            className="flex max-h-[min(var(--available-height),23rem)] flex-1 flex-col text-foreground"
            data-slot="autocomplete-popup"
            {...props}
          >
            {children}
          </AutocompletePrimitive.Popup>
        </span>
      </AutocompletePrimitive.Positioner>
    </AutocompletePrimitive.Portal>
  );
}

export function AutocompleteItem({
  className,
  children,
  ...props
}: AutocompletePrimitive.Item.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.Item
      className={cn(
        "flex min-h-8 cursor-pointer select-none items-center rounded-sm px-2 py-1 text-base outline-none data-disabled:pointer-events-none data-highlighted:bg-wash-hover data-highlighted:text-foreground data-disabled:opacity-64 sm:min-h-7 sm:text-sm",
        className,
      )}
      data-slot="autocomplete-item"
      {...props}
    >
      {children}
    </AutocompletePrimitive.Item>
  );
}

export function AutocompleteSeparator({
  className,
  ...props
}: AutocompletePrimitive.Separator.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.Separator
      className={cn("mx-2 my-1 h-px bg-border last:hidden", className)}
      data-slot="autocomplete-separator"
      {...props}
    />
  );
}

export function AutocompleteGroup({
  className,
  ...props
}: AutocompletePrimitive.Group.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.Group
      className={cn("[[role=group]+&]:mt-1.5", className)}
      data-slot="autocomplete-group"
      {...props}
    />
  );
}

export function AutocompleteGroupLabel({
  className,
  ...props
}: AutocompletePrimitive.GroupLabel.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.GroupLabel
      className={cn(
        "px-2 py-1.5 font-medium text-muted-foreground text-xs",
        className,
      )}
      data-slot="autocomplete-group-label"
      {...props}
    />
  );
}

export function AutocompleteEmpty({
  className,
  ...props
}: AutocompletePrimitive.Empty.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.Empty
      className={cn(
        "not-empty:p-2 text-center text-base text-muted-foreground sm:text-sm",
        className,
      )}
      data-slot="autocomplete-empty"
      {...props}
    />
  );
}

export function AutocompleteRow({
  className,
  ...props
}: AutocompletePrimitive.Row.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.Row
      className={className}
      data-slot="autocomplete-row"
      {...props}
    />
  );
}

export const AutocompleteValue: typeof AutocompletePrimitive.Value =
  AutocompletePrimitive.Value;

export function AutocompleteList({
  className,
  ...props
}: AutocompletePrimitive.List.Props): React.ReactElement {
  return (
    <ScrollArea scrollbarGutter scrollFade>
      <AutocompletePrimitive.List
        className={cn(
          "not-empty:scroll-py-1 not-empty:p-1 in-data-has-overflow-y:pe-3",
          className,
        )}
        data-slot="autocomplete-list"
        {...props}
      />
    </ScrollArea>
  );
}

export function AutocompleteClear({
  className,
  ...props
}: AutocompletePrimitive.Clear.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.Clear
      aria-label="Clear"
      className={cn(
        // A circular secondary button (Button `secondary`'s fill/ink/hover
        // recipe): the clear floats inside the input with no other chrome,
        // so the filled circle is what reads as pressable — and the fill
        // change on hover is the state a bare opacity step never showed.
        // 20px circle, 12px glyph — the touch target stays ≥44px via the
        // pointer-coarse pseudo-element, so the small footprint is visual
        // only.
        // `not-data-visible:hidden`: Base UI keeps the button mounted through
        // its exit transition, so the empty-value state must hide it by state
        // rather than rely on the unmount alone.
        "absolute end-0.5 top-1/2 inline-flex size-5 shrink-0 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-transparent bg-secondary text-secondary-foreground outline-none transition-[color,background-color,box-shadow,opacity] not-data-visible:hidden pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:-translate-x-1/2 pointer-coarse:after:-translate-y-1/2 pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 hover:bg-secondary/90 active:bg-secondary/80 data-pressed:bg-secondary/90 [&_svg:not([class*='size-'])]:size-3 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      data-slot="autocomplete-clear"
      {...props}
    >
      <Icon name="x" />
    </AutocompletePrimitive.Clear>
  );
}

export function AutocompleteStatus({
  className,
  ...props
}: AutocompletePrimitive.Status.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.Status
      className={cn(
        "px-3 py-2 font-medium text-muted-foreground text-xs empty:m-0 empty:p-0",
        className,
      )}
      data-slot="autocomplete-status"
      {...props}
    />
  );
}

export const AutocompleteCollection: typeof AutocompletePrimitive.Collection =
  AutocompletePrimitive.Collection;

export function AutocompleteTrigger({
  className,
  children,
  ...props
}: AutocompletePrimitive.Trigger.Props): React.ReactElement {
  return (
    <AutocompletePrimitive.Trigger
      className={className}
      data-slot="autocomplete-trigger"
      {...props}
    >
      {children}
    </AutocompletePrimitive.Trigger>
  );
}

export const useAutocompleteFilter: typeof AutocompletePrimitive.useFilter =
  AutocompletePrimitive.useFilter;
